/**
 * Tarabalam Component
 * Displays Tara Balam information for the current nakshatra using API
 */

import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Separator } from '../ui/separator';
import { Star, Users, Loader2, ChevronDown, ChevronUp, ChevronRight } from 'lucide-react';
import { SanskritTerm } from '../SanskritTerm/SanskritTerm';
import type { TaraBalamResponse, FavorableNakshatra } from '../../types/api';

interface TarabalamProps {
  locale?: string;
  /** Fetched by DailyPanchangaView by number, with the day context from angas (B-139-REQ). */
  preloadedData?: TaraBalamResponse | null;
  preloadedSecondData?: TaraBalamResponse | null;
  /** Set when that fetch failed. */
  error?: string | null;
  onNavigateToArticle?: (articleId: string) => void;
}

// Group favorable nakshatras by tara name
function groupByTaraName(nakshatras: FavorableNakshatra[]): Map<string, FavorableNakshatra[]> {
  const grouped = new Map<string, FavorableNakshatra[]>();
  
  nakshatras.forEach((nakshatra) => {
    const taraName = nakshatra.tara_name;
    if (!grouped.has(taraName)) {
      grouped.set(taraName, []);
    }
    grouped.get(taraName)!.push(nakshatra);
  });
  
  return grouped;
}

/**
 * Presentational: DailyPanchangaView requests Tārā Bala by number. The component's
 * own fetch posted the display sentence and was removed in B-139-CON.
 */
export const Tarabalam: React.FC<TarabalamProps> = ({ preloadedData, preloadedSecondData, error = null, onNavigateToArticle }) => {
  const data = preloadedData ?? null;
  const secondData = preloadedSecondData ?? null;
  const loading = !data && !error;
  const [isFavorableSectionCollapsed, setIsFavorableSectionCollapsed] = useState<boolean>(true);
  const [isSecondSectionCollapsed, setIsSecondSectionCollapsed] = useState<boolean>(true);
  const [expandedActivities, setExpandedActivities] = useState<Set<number>>(new Set());
  const [expandedTaraTypes, setExpandedTaraTypes] = useState<Set<string>>(new Set());
  const [expandedSecondActivities, setExpandedSecondActivities] = useState<Set<number>>(new Set());
  const [expandedSecondTaraTypes, setExpandedSecondTaraTypes] = useState<Set<string>>(new Set());

  // Group nakshatras by tara name
  const groupedNakshatras = useMemo(() => {
    if (!data) return new Map();
    return groupByTaraName(data.favorable_nakshatras);
  }, [data]);

  const groupedSecondNakshatras = useMemo(() => {
    if (!secondData) return new Map();
    return groupByTaraName(secondData.favorable_nakshatras);
  }, [secondData]);

  // Helper function to render a section
  const renderSection = (
    sectionData: TaraBalamResponse,
    grouped: Map<string, FavorableNakshatra[]>,
    isCollapsed: boolean,
    setIsCollapsed: React.Dispatch<React.SetStateAction<boolean>>,
    expandedTaraTypesSet: Set<string>,
    setExpandedTaraTypesSet: React.Dispatch<React.SetStateAction<Set<string>>>,
    expandedActivitiesSet: Set<number>,
    setExpandedActivitiesSet: React.Dispatch<React.SetStateAction<Set<number>>>,
    endTime?: string,
    headingPrefix?: string
  ) => {
    if (sectionData.favorable_nakshatras.length === 0) return null;

    const toggleTaraType = (taraName: string) => {
      setExpandedTaraTypesSet((prev) => {
        const newExpanded = new Set(prev);
        if (newExpanded.has(taraName)) {
          newExpanded.delete(taraName);
        } else {
          newExpanded.add(taraName);
        }
        return newExpanded;
      });
    };

    const toggleActivities = (taraNumber: number) => {
      setExpandedActivitiesSet((prev) => {
        const newExpanded = new Set(prev);
        if (newExpanded.has(taraNumber)) {
          newExpanded.delete(taraNumber);
        } else {
          newExpanded.add(taraNumber);
        }
        return newExpanded;
      });
    };

    // "Favourable till HH:MM for the nakṣatras below": the time sits next to
    // "favourable" so it can't be read as part of a birth phrase, and the
    // heading points at the badge list under it (B-145; spec §6).
    const headingLead = headingPrefix ? `${headingPrefix} favourable` : 'Favourable';

    return (
      <div>
        {/* Heading */}
        <h4 className="text-sm font-semibold flex items-center gap-2 mb-3 text-green-600 dark:text-green-400">
          <Users className="h-4 w-4" />
          {headingLead}
          {endTime && (
            <>
              {' '}
              <span className="text-sm font-semibold text-muted-foreground">
                till {endTime}
              </span>
            </>
          )}
          {' '}
          <span>for the nakṣatras below</span>
        </h4>
        
        {/* Collapsible nakshatra names list */}
        <button
          onClick={() => setIsCollapsed(!isCollapsed)}
          aria-expanded={!isCollapsed}
          className="w-full flex items-center justify-between hover:bg-accent/50 rounded-md p-2 -m-2 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
          <div className="flex flex-wrap items-center gap-2">
            {sectionData.favorable_nakshatras.map((nakshatra: FavorableNakshatra) => (
              <span
                key={nakshatra.nakshatra_number}
                className="inline-block px-2 py-1 rounded-md bg-muted text-foreground text-xs"
              >
                {nakshatra.nakshatra_name}
              </span>
            ))}
          </div>
          {isCollapsed ? (
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
          ) : (
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          )}
        </button>
        
        {!isCollapsed && (
          <div
            className="mt-3 space-y-2"
            // On expand, bring the revealed content into view (minimal scroll).
            // Also engages the sticky sidebar, which only repositions on scroll.
            ref={(el) => {
              if (el && !el.dataset.revealed) {
                el.dataset.revealed = '1';
                el.scrollIntoView?.({ behavior: 'smooth', block: 'nearest' });
              }
            }}
          >
            {Array.from(grouped.entries()).map(([taraName, nakshatras]) => {
              // Get tara details from first nakshatra (all have same tara properties)
              const firstNakshatra = nakshatras[0];
              const isTaraExpanded = expandedTaraTypesSet.has(taraName);
              const isActivitiesExpanded = expandedActivitiesSet.has(firstNakshatra.tara_number);
              const hasMoreActivities = firstNakshatra.activities && firstNakshatra.activities.length > 3;
              
              // Create comma-separated list of nakshatra names
              const nakshatraNames = nakshatras.map((n: FavorableNakshatra) => n.nakshatra_name).join(', ');
              
              return (
                <div
                  key={taraName}
                  className="rounded-lg border bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800 overflow-hidden"
                >
                  <button
                    onClick={() => toggleTaraType(taraName)}
                    aria-expanded={isTaraExpanded}
                    className="w-full flex items-center justify-between p-3 hover:bg-green-100/50 dark:hover:bg-green-900/30 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded-lg"
                  >
                    <div className="flex items-center gap-2">
                      <Badge className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 text-sm px-3 py-1">
                        {taraName}
                      </Badge>
                      <span className="text-sm font-semibold text-foreground">
                        {nakshatraNames}
                      </span>
                    </div>
                    {isTaraExpanded ? (
                      <ChevronDown className="h-4 w-4 text-muted-foreground" />
                    ) : (
                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    )}
                  </button>
                  
                  {isTaraExpanded && (
                    <div className="px-3 pb-3 space-y-3 border-t border-green-200 dark:border-green-800 pt-3">
                      {firstNakshatra.meaning && (
                        <p className="text-sm text-muted-foreground">{firstNakshatra.meaning}</p>
                      )}
                      
                      {firstNakshatra.activities && firstNakshatra.activities.length > 0 && (
                        <div>
                          <p className="text-sm font-medium text-green-700 dark:text-green-300 mb-2">
                            Good day for:
                          </p>
                          <ul className="text-sm text-muted-foreground space-y-1">
                            {firstNakshatra.activities
                              .slice(0, isActivitiesExpanded ? firstNakshatra.activities.length : 3)
                              .map((activity: string, idx: number) => (
                                <li key={idx} className="flex items-start">
                                  <span className="mr-2">•</span>
                                  <span>{activity}</span>
                                </li>
                              ))}
                            {hasMoreActivities && (
                              <li>
                                {!isActivitiesExpanded ? (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      toggleActivities(firstNakshatra.tara_number);
                                    }}
                                    className="text-xs italic text-green-600 dark:text-green-400 hover:underline flex items-center gap-1 cursor-pointer"
                                  >
                                    +{firstNakshatra.activities.length - 3} more
                                  </button>
                                ) : (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      toggleActivities(firstNakshatra.tara_number);
                                    }}
                                    className="text-xs italic text-green-600 dark:text-green-400 hover:underline flex items-center gap-1 cursor-pointer"
                                  >
                                    <ChevronUp className="h-3 w-3" />
                                    Show less
                                  </button>
                                )}
                              </li>
                            )}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <Card className="w-full">
        <CardHeader>
          <CardTitle className="text-lg font-semibold mb-2 flex items-center gap-2 text-brand dark:text-brand-dark">
            <Star className="h-5 w-5" />
            <SanskritTerm term="Tārā Balam" explanation="Tārā Balam is a nakṣatra-based system that determines favourable and unfavourable periods based on the relationship between your birth nakṣatra and the current nakṣatra." />
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div role="status" aria-live="polite" className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-primary" aria-hidden="true" />
            <span className="ml-2 text-muted-foreground">Loading Tārā Balam...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="w-full">
        <CardHeader>
          <CardTitle className="text-lg font-semibold mb-2 flex items-center gap-2 text-brand dark:text-brand-dark">
            <Star className="h-5 w-5" />
            <SanskritTerm term="Tārā Balam" explanation="Tārā Balam is a nakṣatra-based system that determines favourable and unfavourable periods based on the relationship between your birth nakṣatra and the current nakṣatra." />
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-red-500">Error loading Tārā Balam: {error}</p>
        </CardContent>
      </Card>
    );
  }

  if (!data) {
    return null;
  }

  return (
    <Card className="w-full kg-warm rounded-2xl" style={{ background: 'var(--kg-card)', border: '1px solid var(--kg-line)', boxShadow: 'var(--kg-shadow)' }}>
      <CardHeader className="pt-4 pb-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle className="font-fraunces text-[18px] font-medium" style={{ color: 'var(--kg-ink)' }}>
            <SanskritTerm term="Tārā Balam" explanation="Tārā Balam is a nakṣatra-based system that determines favourable and unfavourable periods based on the relationship between your birth nakṣatra and the current nakṣatra." />
          </CardTitle>
          {onNavigateToArticle && (
            <button
              onClick={() => onNavigateToArticle('how-to-tarabalam')}
              className="kg-link text-[12.5px] font-semibold"
            >
              What is Tārā Balam? &rarr;
            </button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4 pb-4">
        {/* First Section - First Nakshatra */}
        {data && data.favorable_nakshatras.length > 0 && (
          <>
            {renderSection(
              data,
              groupedNakshatras,
              isFavorableSectionCollapsed,
              setIsFavorableSectionCollapsed,
              expandedTaraTypes,
              setExpandedTaraTypes,
              expandedActivities,
              setExpandedActivities,
              data.first_nakshatra_end_time ?? undefined
            )}
          </>
        )}

        {/* Second Section - Second Nakshatra (if exists) */}
        {data && data.second_nakshatra_name && (
          <>
            {secondData ? (
              secondData.favorable_nakshatras.length > 0 ? (
                <>
                  <Separator />
                  {renderSection(
                    secondData,
                    groupedSecondNakshatras,
                    isSecondSectionCollapsed,
                    setIsSecondSectionCollapsed,
                    expandedSecondTaraTypes,
                    setExpandedSecondTaraTypes,
                    expandedSecondActivities,
                    setExpandedSecondActivities,
                    data.second_nakshatra_end_time ?? undefined,
                    'Afterwards'
                  )}
                </>
              ) : (
                <div className="p-4 rounded-lg border bg-muted/30">
                  <p className="text-sm text-muted-foreground">
                    No favorable nakshatras found for {data.second_nakshatra_name}.
                  </p>
                </div>
              )
            ) : (
              <div className="p-4 rounded-lg border bg-muted/30">
                <p className="text-sm text-muted-foreground">
                  Loading data for {data.second_nakshatra_name}...
                </p>
              </div>
            )}
          </>
        )}

        {data && data.favorable_nakshatras.length === 0 && (!data.second_nakshatra_name || !secondData || secondData.favorable_nakshatras.length === 0) && (
          <div className="p-4 rounded-lg border bg-muted/30">
            <p className="text-sm text-muted-foreground">
              No favorable nakshatras found for the current nakshatra.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default Tarabalam;
